import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TransferHead } from './TransferHead';
import { ILinkedAccount } from '../../../../types';
import { TransferBody } from './TransferBody';
import { TransferFooter } from './TransferFooter';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { TransferDetailsConfirmation } from '../TransferDetailsConfirmation';
import { useGetEstimatedArrivalQuery } from '../../../../hooks/query/useGetEstimatedArrivalQuery';
import { useStore } from '../../../../store';
import { refactoredTimeAndDate } from '../../../../helpers/timeFormatter.helper';
import { DATE_FORMAT } from '../../../../constants/general';
import { useAchInLimitQuery } from '../../../../hooks/query/useAchInLimitQuery';
import { TEXT, INVALID_TRANSFER_VALUES } from '../../constants';
import { FlowContext, UploadContext } from '../../pages';
import { isParseAble } from '../../../../helpers/currency.helper';
import { useGetArrivalDateForAddMoney } from '../../hooks/useGetArrivalDateForAddMoney';

const {
  PAGES: {
    POST_LINKED: {
      MONEY_TRANSFER: {
        BODY: { MAX_TRANSFER, MAX_TRANSFER_CHECKING_ACCOUNT },
      },
    },
  },
} = TEXT;

interface IProps {
  activeLinkAccount: ILinkedAccount;
  displayTitle: string;
  handleSuccessTransfer: (estimatedArrivalDate: string, amount: string, firstTimeMessage?: string) => void;
}

interface IErrorInternal {
  content: string;
  isDismissable: boolean;
}

export const MoneyTransfer = ({ activeLinkAccount, displayTitle, handleSuccessTransfer }: IProps) => {
  const [amountToTransfer, setAmountToTransfer] = useState<string>('');
  const [maxAmountLimit, setMaxAmountLimit] = useState<number>(0);
  const [error, setError] = useState<IErrorInternal>({ content: '', isDismissable: true });
  const [estimatedArrivalDate, setEstimatedArrivalDate] = useState<string>('');
  const [firstTimeMessage, setFirstTimeMessage] = useState<string>('');
  const [addMoneyError, setAddMoneyError] = useState<string>('');
  const [isUserMessage, setIsUserMessage] = useState<boolean>(false);
  const { t } = useTranslation();
  const { getPrimaryAccount } = useStore((state) => state);
  const { customerId } = useStore((state) => state.user);

  const { balance } = getPrimaryAccount();

  const isAddMoneyFlow = useContext(FlowContext);
  const { setAccounts } = useContext(UploadContext);

  function handleRequiredLogin() {
    setAccounts(null);
    toast.error('To ensure your account’s security, we need you to reconnect it', {
      position: 'bottom-center',
      autoClose: 3500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });
  }

  const { refetch: getEstimatedArrival, isLoading: isRefetching } = useGetEstimatedArrivalQuery(customerId, () =>
    handleRequiredLogin());
  const { mutateAsync: getEstimatedArrivalForAddMoney, isLoading: isGettingArrivalDate } = useGetArrivalDateForAddMoney(
    {
      onClearAccounts: () => handleRequiredLogin(),
      onError: (errMsg: string) => {
        setError({ content: errMsg, isDismissable: true });
      },
    },
  );
  const {
    refetch: getAchInLimit,
    isLoading,
    isRefetching: isRefetchAchLimits,
  } = useAchInLimitQuery(
    { customerId, isAddMoneyFlow },
    (amount: number) => {
      setMaxAmountLimit(amount);
    },
    (_e: IErrorInternal) => {
      setError({ content: _e.content, isDismissable: _e.isDismissable });
      if (!_e.isDismissable) {
        setAddMoneyError(_e.content);
      }
    },
  );

  const isAllowTransfer = useMemo(() => {
    if (amountToTransfer && !error.content) {
      if (amountToTransfer === INVALID_TRANSFER_VALUES.ZERO) {
        return false;
      }
      if (amountToTransfer.slice(-1) === '.') {
        return false;
      }
      return true;
    }
    return false;
  }, [amountToTransfer, error]);

  const handleFocus = () => {
    if (isLoading || isRefetching || isGettingArrivalDate || !error.isDismissable) return;
    if (error.isDismissable) {
      setError({ content: '', isDismissable: true });
    }
  };

  const handleAmountToTransfer = (amount: string) => {
    if (amount.startsWith('.')) amount = `0${amount}`;
    setAmountToTransfer(amount);
  };
  const getFormattedDate = (rawDate: string, firstTimeAchMessage: string | null) => {
    setEstimatedArrivalDate(refactoredTimeAndDate(rawDate, 'PlaidTransferConfirmation', DATE_FORMAT.LETTERS));
    setFirstTimeMessage(firstTimeAchMessage || '');
    setIsUserMessage(true);
  };

  const handleTransfer = () => {
    setAddMoneyError('');
    if (isAddMoneyFlow) {
      getEstimatedArrivalForAddMoney({
        linkedAccountId: activeLinkAccount.externalId,
        amount: Number(amountToTransfer),
      }).then((res) => {
        getFormattedDate(res.clearDate, res.firstTimeACHHoldDaysMessage || null);
      });
    } else {
      getEstimatedArrival().then((data) => {
        const {
          data: { clearDate },
        } = data;
        getFormattedDate(clearDate, null);
      });
    }
  };

  const handleCloseUserMessage = () => {
    setIsUserMessage(false);
    setAddMoneyError('');
  };

  const onFlowSucceed = () => {
    setIsUserMessage(false);
    handleSuccessTransfer(estimatedArrivalDate, amountToTransfer, firstTimeMessage);
  };

  const handleMoneyTransferError = (errContent: string) => {
    setAddMoneyError(errContent);
    setError({ content: errContent, isDismissable: true });
    setIsUserMessage(false);
  };

  useEffect(() => {
    getAchInLimit();
  }, []);

  useEffect(() => {
    setAmountToTransfer('');
    if (!error.isDismissable) return;
    setError({ content: '', isDismissable: true });
  }, [activeLinkAccount]);

  useEffect(() => {
    if (!amountToTransfer) return;
    const cleanValue = amountToTransfer;
    if (!isParseAble(cleanValue)) return;
    const amountInt = JSON.parse(cleanValue);
    if (!isAddMoneyFlow && amountInt > balance) {
      setError({ content: t(MAX_TRANSFER_CHECKING_ACCOUNT), isDismissable: true });
    } else if (amountInt > maxAmountLimit) {
      setError({ content: `${t(MAX_TRANSFER)}${maxAmountLimit}`, isDismissable: true });
    } else if (error.content) {
      setError({ content: '', isDismissable: true });
    }
  }, [amountToTransfer]);
  return (
    <>
      <TransferHead accountInfo={displayTitle} />
      <TransferBody
        onSubmit={handleTransfer}
        balance={!isAddMoneyFlow ? balance : 0}
        isDisabledInput={!error.isDismissable}
        error={error.content}
        onFocus={handleFocus}
        accountId={activeLinkAccount.id}
        onTransferValue={handleAmountToTransfer}
      />
      <TransferFooter
        isLoading={isRefetching || isGettingArrivalDate || isLoading || isRefetchAchLimits}
        isTransfer={isAllowTransfer}
        onSubmit={handleTransfer}
      />
      {isUserMessage && (
        <UserMessage onClick={handleCloseUserMessage} id='review-transfer-modal'>
          {!addMoneyError && (
            <TransferDetailsConfirmation
              firstTimeMessage={firstTimeMessage}
              handleOnError={handleMoneyTransferError}
              handleSuccessTransfer={onFlowSucceed}
              estimatedArrival={estimatedArrivalDate}
              internalCloseModal={handleCloseUserMessage}
              transferValue={amountToTransfer}
              linkAccountData={activeLinkAccount}
            />
          )}
        </UserMessage>
      )}
    </>
  );
};
